<script setup>
import { ref } from 'vue'

const connections = ref([])
const showNewConnectionForm = ref(false)

const newConnection = ref({
  name: '',
  host: '',
  port: '3306',
  username: '',
  password: '',
  useSSH: false,
  sshHost: '',
  sshUsername: '',
  sshPassword: '',
  sshKeyFile: null
})

const addConnection = async () => {
  try {
    // Here you would typically make an API call to test and save the connection
    connections.value.push({
      ...newConnection.value,
      id: Date.now(),
      status: 'connected'
    })
    showNewConnectionForm.value = false
    newConnection.value = {
      name: '',
      host: '',
      port: '3306',
      username: '',
      password: '',
      useSSH: false,
      sshHost: '',
      sshUsername: '',
      sshPassword: '',
      sshKeyFile: null
    }
  } catch (error) {
    console.error('Failed to add connection:', error)
  }
}

const handleFileUpload = (event) => {
  newConnection.value.sshKeyFile = event.target.files[0]
}
</script>

<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-800">Database Connections</h2>
      <button
        @click="showNewConnectionForm = true"
        class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
      >
        <i class="fas fa-plus mr-2"></i>New Connection
      </button>
    </div>

    <!-- Connection List -->
    <div class="grid grid-cols-1 gap-4">
      <div
        v-for="connection in connections"
        :key="connection.id"
        class="p-4 border rounded-lg hover:shadow-md transition-shadow"
      >
        <div class="flex justify-between items-center">
          <div>
            <h3 class="font-semibold text-lg">{{ connection.name }}</h3>
            <p class="text-gray-600">{{ connection.host }}:{{ connection.port }}</p>
          </div>
          <div class="flex space-x-2">
            <span
              :class="connection.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
              class="px-2 py-1 rounded-full text-sm"
            >
              {{ connection.status }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- New Connection Form Modal -->
    <div v-if="showNewConnectionForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div class="bg-white rounded-lg p-6 w-full max-w-2xl">
        <h3 class="text-xl font-bold mb-4">New Database Connection</h3>
        
        <form @submit.prevent="addConnection" class="space-y-4">
          <!-- Basic Connection Details -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Connection Name</label>
              <input
                v-model="newConnection.name"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Host</label>
              <input
                v-model="newConnection.host"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Port</label>
              <input
                v-model="newConnection.port"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Username</label>
              <input
                v-model="newConnection.username"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Password</label>
              <input
                v-model="newConnection.password"
                type="password"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              >
            </div>
          </div>

          <!-- SSH Tunnel Option -->
          <div class="mt-4">
            <label class="flex items-center">
              <input
                v-model="newConnection.useSSH"
                type="checkbox"
                class="rounded border-gray-300 text-pink-500 focus:ring-pink-500"
              >
              <span class="ml-2">Use SSH Tunnel</span>
            </label>
          </div>

          <!-- SSH Configuration (shown only if SSH is enabled) -->
          <div v-if="newConnection.useSSH" class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">SSH Host</label>
                <input
                  v-model="newConnection.sshHost"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">SSH Username</label>
                <input
                  v-model="newConnection.sshUsername"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                >
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">SSH Password</label>
              <input
                v-model="newConnection.sshPassword"
                type="password"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">SSH Key File</label>
              <input
                type="file"
                @change="handleFileUpload"
                class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100"
              >
            </div>
          </div>

          <div class="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              @click="showNewConnectionForm = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
            >
              Add Connection
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template> 